import { formatPhone, getPhoneTypeLabel } from '@/lib/phoneUtils';
import type { CustomerPhoneSummary } from '../types';

interface CustomerPhoneListProps {
  phones: CustomerPhoneSummary[];
  onSetPrimary?: (phoneId: string) => void;
  onEdit?: (phone: CustomerPhoneSummary) => void;
  onDelete?: (phoneId: string) => void;
  isUpdating?: boolean;
  compact?: boolean;
}

export function CustomerPhoneList({
  phones,
  onSetPrimary,
  onEdit,
  onDelete,
  isUpdating = false,
  compact = false,
}: CustomerPhoneListProps) {
  if (phones.length === 0) {
    return (
      <p className="text-sm text-secondary-500 italic">No phone numbers</p>
    );
  }

  // Sort with primary first
  const sortedPhones = [...phones].sort((a, b) => {
    if (a.is_primary && !b.is_primary) return -1;
    if (!a.is_primary && b.is_primary) return 1;
    return 0;
  });

  if (compact) {
    return (
      <div className="space-y-1">
        {sortedPhones.map((phone) => (
          <div key={phone.id} className="flex items-center gap-2 text-sm">
            <span className="font-medium">{formatPhone(phone.phone_number)}</span>
            <span className="text-secondary-500">
              ({getPhoneTypeLabel(phone.phone_type)})
            </span>
            {phone.is_primary && (
              <span className="text-xs bg-primary-100 text-primary-700 px-1.5 py-0.5 rounded">
                Primary
              </span>
            )}
            {phone.can_sms && (
              <span className="text-xs text-green-600" title="Can receive SMS">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
              </span>
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {sortedPhones.map((phone) => (
        <div
          key={phone.id}
          className="flex items-center justify-between p-3 bg-secondary-50
                     rounded-lg border border-secondary-200"
        >
          {/* Phone Info */}
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="font-medium text-secondary-900">
                {formatPhone(phone.phone_number)}
              </span>
              <span className="text-sm text-secondary-500">
                {getPhoneTypeLabel(phone.phone_type)}
              </span>
              {phone.is_primary && (
                <span className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full">
                  Primary
                </span>
              )}
            </div>

            {/* Additional Info */}
            <div className="flex items-center gap-3 mt-1">
              {phone.label && (
                <span className="text-sm text-secondary-600">{phone.label}</span>
              )}
              {phone.can_sms && (
                <span className="flex items-center gap-1 text-xs text-green-600">
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                  </svg>
                  SMS
                </span>
              )}
              {phone.notes && (
                <span className="text-xs text-secondary-500 italic">
                  {phone.notes}
                </span>
              )}
            </div>
          </div>

          {/* Actions */}
          {(onSetPrimary || onEdit || onDelete) && (
            <div className="flex items-center gap-1">
              {/* Set Primary */}
              {onSetPrimary && !phone.is_primary && (
                <button
                  type="button"
                  onClick={() => onSetPrimary(phone.id)}
                  className="p-1.5 text-secondary-400 hover:text-primary-600
                             hover:bg-primary-50 rounded transition-colors"
                  disabled={isUpdating}
                  title="Set as primary"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                    />
                  </svg>
                </button>
              )}

              {/* Edit */}
              {onEdit && (
                <button
                  type="button"
                  onClick={() => onEdit(phone)}
                  className="p-1.5 text-secondary-400 hover:text-primary-600
                             hover:bg-primary-50 rounded transition-colors"
                  disabled={isUpdating}
                  title="Edit phone"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                </button>
              )}

              {/* Delete */}
              {onDelete && (
                <button
                  type="button"
                  onClick={() => onDelete(phone.id)}
                  className="p-1.5 text-secondary-400 hover:text-red-600
                             hover:bg-red-50 rounded transition-colors"
                  disabled={isUpdating}
                  title="Delete phone"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
